import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import type { PositionPoint } from '@/types/forklift'

export interface AnimationState {
  isPlaying: boolean
  currentIndex: number
  speed: number // 1x, 2x, 4x, 8x
  progress: number // 0-100
  currentTime: number // 現在の仮想時刻（ミリ秒）
}

interface UseTrajectoryAnimationOptions {
  positions: PositionPoint[]
}

interface UseTrajectoryAnimationReturn {
  state: AnimationState
  currentPosition: PositionPoint | null
  visiblePath: PositionPoint[]
  play: () => void
  pause: () => void
  reset: () => void
  setSpeed: (speed: number) => void
  seekTo: (index: number) => void
  seekToProgress: (progress: number) => void
  seekToTime: (time: Date) => void
}

export function useTrajectoryAnimation({
  positions,
}: UseTrajectoryAnimationOptions): UseTrajectoryAnimationReturn {
  const [state, setState] = useState<AnimationState>({
    isPlaying: false,
    currentIndex: 0,
    speed: 1,
    progress: 0,
    currentTime: 0,
  })

  const speedRef = useRef(state.speed)

  // speedの変更をrefに反映（次フレームから適用）
  useEffect(() => {
    speedRef.current = state.speed
  }, [state.speed])

  // positionsの変更を検出するためのキー
  const positionsKey = useMemo(() => {
    if (positions.length === 0) return ''
    const first = positions[0]
    const last = positions[positions.length - 1]
    return `${positions.length}-${first?.timestamp}-${last?.timestamp}`
  }, [positions])

  // 現在位置
  const currentPosition = positions[state.currentIndex] ?? null

  // 表示するパス（開始から現在位置まで）
  const visiblePath = positions.slice(0, state.currentIndex + 1)

  // 進捗率を計算
  const calculateProgress = useCallback(
    (index: number) => {
      if (positions.length <= 1) return 100
      return (index / (positions.length - 1)) * 100
    },
    [positions.length]
  )

  // 再生
  const play = useCallback(() => {
    setState((prev) => ({ ...prev, isPlaying: true }))
  }, [])

  // 一時停止
  const pause = useCallback(() => {
    setState((prev) => ({ ...prev, isPlaying: false }))
  }, [])

  // リセット
  const reset = useCallback(() => {
    const startTime =
      positions.length > 0 ? new Date(positions[0].timestamp).getTime() : 0
    setState({
      isPlaying: false,
      currentIndex: 0,
      speed: state.speed,
      progress: 0,
      currentTime: startTime,
    })
  }, [state.speed, positions])

  // 速度変更
  const setSpeed = useCallback((speed: number) => {
    setState((prev) => ({ ...prev, speed }))
  }, [])

  // インデックス指定でシーク
  const seekTo = useCallback(
    (index: number) => {
      const clampedIndex = Math.max(0, Math.min(index, positions.length - 1))
      const pos = positions[clampedIndex]
      const newTime = pos ? new Date(pos.timestamp).getTime() : 0
      setState((prev) => ({
        ...prev,
        currentIndex: clampedIndex,
        progress: calculateProgress(clampedIndex),
        currentTime: newTime,
      }))
    },
    [positions, calculateProgress]
  )

  // 進捗率指定でシーク（時間ベース）
  const seekToProgress = useCallback(
    (progress: number) => {
      if (positions.length === 0) return

      const startTime = new Date(positions[0].timestamp).getTime()
      const endTime = new Date(
        positions[positions.length - 1].timestamp
      ).getTime()
      const targetTime = startTime + (progress / 100) * (endTime - startTime)

      // 対応するインデックスを探す
      let targetIndex = 0
      for (let i = 0; i < positions.length; i++) {
        const posTime = new Date(positions[i].timestamp).getTime()
        if (posTime > targetTime) {
          targetIndex = Math.max(0, i - 1)
          break
        }
        targetIndex = i
      }

      setState((prev) => ({
        ...prev,
        currentIndex: targetIndex,
        progress,
        currentTime: targetTime,
      }))
    },
    [positions]
  )

  // 時刻指定でシーク（最も近いポジションにジャンプ）
  const seekToTime = useCallback(
    (time: Date) => {
      if (positions.length === 0) return

      const targetTime = time.getTime()
      let closestIndex = 0
      let closestDiff = Infinity

      for (let i = 0; i < positions.length; i++) {
        const posTime = new Date(positions[i].timestamp).getTime()
        const diff = Math.abs(posTime - targetTime)
        if (diff < closestDiff) {
          closestDiff = diff
          closestIndex = i
        }
      }

      const startTime = new Date(positions[0].timestamp).getTime()
      const endTime = new Date(
        positions[positions.length - 1].timestamp
      ).getTime()
      const newProgress =
        ((targetTime - startTime) / (endTime - startTime)) * 100

      setState((prev) => ({
        ...prev,
        currentIndex: closestIndex,
        progress: Math.max(0, Math.min(100, newProgress)),
        currentTime: targetTime,
      }))
    },
    [positions]
  )

  // タイムスタンプからインデックスを探す
  const findIndexForTime = useCallback(
    (time: number): number => {
      if (positions.length === 0) return 0

      for (let i = 0; i < positions.length; i++) {
        const posTime = new Date(positions[i].timestamp).getTime()
        if (posTime > time) {
          return Math.max(0, i - 1)
        }
      }
      return positions.length - 1
    },
    [positions]
  )

  // 時間範囲を取得
  const timeRange = useMemo(() => {
    if (positions.length === 0) return { start: 0, end: 0 }
    const start = new Date(positions[0].timestamp).getTime()
    const end = new Date(positions[positions.length - 1].timestamp).getTime()
    return { start, end }
  }, [positions])

  // アニメーションループ（時間ベース）
  // 固定間隔でタイマーを進め、その時刻に対応するポジションを表示
  const TICK_INTERVAL = 50 // 50msごとに更新

  useEffect(() => {
    if (!state.isPlaying || positions.length === 0) {
      return
    }

    const intervalId = setInterval(() => {
      setState((prev) => {
        // 仮想時刻を進める（速度に応じて）
        const newTime = prev.currentTime + TICK_INTERVAL * speedRef.current

        // 終了チェック
        if (newTime >= timeRange.end) {
          return {
            ...prev,
            isPlaying: false,
            currentIndex: positions.length - 1,
            progress: 100,
            currentTime: timeRange.end,
          }
        }

        // 現在時刻に対応するインデックスを探す
        const newIndex = findIndexForTime(newTime)
        const newProgress =
          ((newTime - timeRange.start) / (timeRange.end - timeRange.start)) *
          100

        return {
          ...prev,
          currentIndex: newIndex,
          progress: newProgress,
          currentTime: newTime,
        }
      })
    }, TICK_INTERVAL)

    return () => {
      clearInterval(intervalId)
    }
  }, [state.isPlaying, positions.length, timeRange, findIndexForTime])

  // positionsが変更されたらリセット
  useEffect(() => {
    const startTime =
      positions.length > 0 ? new Date(positions[0].timestamp).getTime() : 0
    setState({
      isPlaying: false,
      currentIndex: 0,
      speed: 1,
      progress: 0,
      currentTime: startTime,
    })
  }, [positionsKey, positions])

  return {
    state,
    currentPosition,
    visiblePath,
    play,
    pause,
    reset,
    setSpeed,
    seekTo,
    seekToProgress,
    seekToTime,
  }
}
